import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Loader2 } from 'lucide-react';
import FieldRenderer from './FieldRenderer.jsx';

const FormBuilder = ({ 
  schema, 
  mode, 
  initialData, 
  onSave, 
  onClose, 
  isDarkMode,
  title,
  apiEndpoint 
}) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Estado para catálogos de admin clientes
  const [catalogosAdminClientes, setCatalogosAdminClientes] = useState({});
  const [loadingCatalogos, setLoadingCatalogos] = useState(false);
  const [catalogosLoaded, setCatalogosLoaded] = useState(false);

  // Verificar si el schema necesita catálogos de admin clientes
  const needsAdminClientesCatalogos = schema.fields.some(field => 
    field.component?.name === 'SelectorAdminClientes' || 
    ['nosucursal', 'idsatregfis', 'idsatmetpago', 'idsatusocfdi'].includes(field.name)
  );

  // Cargar catálogos de admin clientes una sola vez
  useEffect(() => {
    if (needsAdminClientesCatalogos && !catalogosLoaded) {
      setCatalogosLoaded(true);

      const loadCatalogosAdminClientes = async () => {
        setLoadingCatalogos(true);
        try {
          const token = sessionStorage.getItem('mapasac_access_token');
          const response = await fetch('/api/cxc/adminclientes?action=GET_ALL_CATALOGOS', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const result = await response.json();
            setCatalogosAdminClientes(result.data);
          } else {
            console.error('Error cargando catálogos admin clientes');
          }
        } catch (error) {
          console.error('Error:', error);
        } finally {
          setLoadingCatalogos(false);
        }
      };

      loadCatalogosAdminClientes();
    }
  }, [needsAdminClientesCatalogos, catalogosLoaded]);

  // Inicializar formData
  useEffect(() => {
  if (mode === 'edit' || mode === 'view') {
    const dataWithUbicacion = { ...initialData };
    
    // Si existen campos geográficos individuales, crear objeto ubicacion
    if (initialData?.nopais || initialData?.claveedos || initialData?.nomunicip) {
      dataWithUbicacion.ubicacion = {
        pais: initialData.nopais || '',
        estado: initialData.claveedos || '',
        municipio: initialData.nomunicip || ''
      };
    }
    
      setFormData(dataWithUbicacion);
  } else if (mode === 'create') {
    // Valores por defecto
    const defaultData = {};
    schema.fields.forEach(field => {
      if (field.type === 'checkbox') {
        defaultData[field.name] = 1; // Activo por defecto
      } else if (field.name === 'ubicacion') {
        defaultData[field.name] = { pais: '', estado: '', municipio: '' };
      } else if (field.name === 'idsubmodulos') {
        defaultData[field.name] = []; // Array vacío para submódulos múltiples
      } else {
        defaultData[field.name] = '';
      }
    });
    setFormData(defaultData);
  }
  }, [mode, initialData, schema.fields]);

  // Limpiar submódulos cuando cambie el módulo
  useEffect(() => {
    if (formData.idmodulos && formData.idsubmodulos && Array.isArray(formData.idsubmodulos)) {
      setFormData(prev => ({
        ...prev,
        idsubmodulos: [] // Limpiar submódulos seleccionados
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.idmodulos]);

  // Validar formulario
  const isFormValid = () => {
    const requiredFields = schema.fields.filter(field => field.required);
    return requiredFields.every(field => {
      const value = formData[field.name];
      
      // Validación especial para arrays (submódulos múltiples)
      if (field.name === 'idsubmodulos' && Array.isArray(value)) {
        return value.length > 0;
      }
      
      return value !== null && value !== undefined && value !== '';
    });
  };

  // Manejar cambios en campos
  const handleFieldChange = (fieldName, value) => {
    const fieldSchema = schema.fields.find(field => field.name === fieldName);
    let newValue = value;
    if (fieldSchema && fieldSchema.format === 'uppercase' && typeof value === 'string') {
      newValue = value.toUpperCase();
    }

    setFormData(prev => ({
      ...prev,
      [fieldName]: newValue
    }));
  };

  // Manejar guardado
  const handleSave = async () => {
    setSaving(true);

    try {
      if ((mode === 'create' || mode === 'edit') && title === 'Usuario') {
      }

      // Validaciones básicas
      const requiredFields = schema.fields.filter(field => field.required);
      for (const field of requiredFields) {
        const value = formData[field.name];
        
        // Validación especial para submódulos múltiples
        if (field.name === 'idsubmodulos' && Array.isArray(value)) {
          if (value.length === 0) {
            toast.error('Debe seleccionar al menos un submódulo');
            setSaving(false);
            return;
          }
        } else if (!value || value.toString().trim() === '') {
          toast.error(`${field.label} es requerido`);
          setSaving(false);
          return;
        }
      }

      // Transformar datos antes de enviar
      const dataToSend = { ...formData };

      delete dataToSend.confirmPassword;

      if (mode === 'edit' && (!dataToSend.password || dataToSend.password.trim() === '')) 
        {
          delete dataToSend.password;
        }
    
      // Si existe campo ubicacion, extraer los valores individuales
      if (formData.ubicacion) {
        dataToSend.nopais = formData.ubicacion.pais;
        dataToSend.claveedos = formData.ubicacion.estado;
        dataToSend.nomunicip = formData.ubicacion.municipio;
        delete dataToSend.ubicacion; // Remover el objeto ubicacion
      }

      // Llamar función de guardado
      await onSave(dataToSend);
      
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Manejar eliminación
  const handleDelete = async () => {
    setSaving(true);
    try {
      await onSave(formData); // onSave manejará la lógica de delete
    } catch (error) {
      toast.error('Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  const isReadOnly = mode === 'view';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden`}>
        
        {/* Header */}
        <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            {mode === 'create' && `Crear ${title}`}
            {mode === 'edit' && `Editar ${title}`}
            {mode === 'view' && `Ver ${title}`}
            {mode === 'delete' && `Eliminar ${title}`}
          </h3>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[calc(90vh-140px)] overflow-y-auto">
          {mode === 'delete' ? (
            <div>
              <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                ¿Estás seguro de que deseas eliminar este {title.toLowerCase()}?
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                Esta acción no se puede deshacer.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-4 auto-fit-minmax-250">
                {schema.fields.map(field => {
                  const gridClass = field.gridSpan === 'full' ? 'col-span-full' : '';
    
                  return (
                    <div key={field.name} className={gridClass}>
                      <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
        
                      {field.type === 'checkbox' ? (
                        <div className="flex items-center space-x-3">
                          <FieldRenderer
                            field={field}
                            value={formData[field.name]}
                            onChange={(value) => handleFieldChange(field.name, value)}
                            disabled={isReadOnly || saving}
                            isDarkMode={isDarkMode}
                            catalogosAdminClientes={catalogosAdminClientes}
                            loadingCatalogos={loadingCatalogos}
                          />
                          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {field.label}
                          </span>
                        </div>
                      ) : (
                        <FieldRenderer
                          field={field}
                          value={formData[field.name]}
                          onChange={(value) => handleFieldChange(field.name, value)}
                          disabled={isReadOnly || saving}
                          isDarkMode={isDarkMode}
                          showPassword={field.type === 'password' ? showPassword : undefined}
                          setShowPassword={field.type === 'password' ? setShowPassword : undefined}
                          // Props adicionales para componentes custom
                          {...(field.name === 'idsubmodulos' ? { idmodulos: formData.idmodulos } : {})}
                          // Props para catálogos admin clientes
                          catalogosAdminClientes={catalogosAdminClientes}
                          loadingCatalogos={loadingCatalogos}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-end space-x-3`}>
          {mode === 'view' ? (
            <button
              onClick={onClose}
              className={`w-24 px-4 py-2 text-white rounded-lg transition-colors ${
                isDarkMode ? 'bg-gray-600 hover:bg-gray-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              Cerrar
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={saving}
                className={`w-24 px-4 py-2 border rounded-lg disabled:opacity-50 transition-colors ${
                  isDarkMode ? 'text-gray-300 border-gray-600 bg-gray-800 hover:bg-gray-700' : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Cancelar
              </button>
              
              {mode === 'delete' ? (
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className={`w-24 px-4 py-2 text-white rounded-lg disabled:opacity-50 flex items-center justify-center transition-colors ${
                    isDarkMode ? 'bg-red-500 hover:bg-red-600' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {saving && <Loader2 className="animate-spin mr-2" size={16} />}
                  Eliminar
                </button>
              ) : (
                <button
                  onClick={handleSave}
                  disabled={saving || !isFormValid()}
                  className={`w-24 px-4 py-2 text-white rounded-lg disabled:opacity-50 flex items-center justify-center transition-colors ${
                    isDarkMode ? 'bg-gray-600 hover:bg-gray-700' : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {saving && <Loader2 className="animate-spin mr-2" size={16} />}
                  {mode === 'create' ? 'Crear' : 'Guardar'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormBuilder;